import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';

/**
 * RegisterPage — account creation with email/password.
 * Shows password requirements and a success message on completion.
 */
export function RegisterPage(): React.JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [earlyTester, setEarlyTester] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tierDowngraded, setTierDowngraded] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!isPasswordValid(password)) {
      setError('Password does not meet requirements.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.post<{ tierDowngraded?: boolean }>('/auth/register', {
        displayName,
        email,
        password,
        earlyTester,
      });
      if (response?.tierDowngraded === true) {
        setTierDowngraded(true);
      }
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setError(body?.message ?? 'Registration failed. Please try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised p-8 text-center shadow-sm">
          <div className="mb-4 text-4xl">🎉</div>
          <h1 className="mb-2 text-xl font-bold text-text-primary">Account created!</h1>
          {tierDowngraded ? (
            <p className="text-sm text-text-secondary">
              Early Tester spots are full — your account has been created as a Member.
            </p>
          ) : (
            <p className="text-sm text-text-secondary">
              Your account has been created successfully. You can now log in.
            </p>
          )}
          <Link
            to="/login"
            className="mt-6 inline-block text-sm font-medium text-byron underline hover:text-byron-accent"
          >
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised p-8 shadow-sm">
        <h1 className="mb-6 text-center text-2xl font-bold text-text-primary">Join Us</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="reg-name" className="mb-1 block text-sm font-medium text-text-secondary">
              Display Name
            </label>
            <input
              id="reg-name"
              type="text"
              required
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-byron focus:outline-none focus:ring-1 focus:ring-byron"
              placeholder="Your name"
            />
          </div>

          <div>
            <label htmlFor="reg-email" className="mb-1 block text-sm font-medium text-text-secondary">
              Email
            </label>
            <input
              id="reg-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-byron focus:outline-none focus:ring-1 focus:ring-byron"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="reg-password" className="mb-1 block text-sm font-medium text-text-secondary">
              Password
            </label>
            <input
              id="reg-password"
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-byron focus:outline-none focus:ring-1 focus:ring-byron"
              placeholder="••••••••"
            />
            <p className="mt-1 text-xs text-text-muted">
              12+ characters, with uppercase, lowercase, digit, and special character.
            </p>
          </div>

          <div>
            <label htmlFor="reg-confirm" className="mb-1 block text-sm font-medium text-text-secondary">
              Confirm Password
            </label>
            <input
              id="reg-confirm"
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-byron focus:outline-none focus:ring-1 focus:ring-byron"
              placeholder="••••••••"
            />
          </div>

          {/* Early Tester checkbox + consent text */}
          <div className="flex items-start gap-2">
            <input
              id="reg-early-tester"
              type="checkbox"
              checked={earlyTester}
              onChange={(e) => setEarlyTester(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border text-byron focus:ring-byron"
            />
            <div>
              <label htmlFor="reg-early-tester" className="text-sm font-medium text-text-secondary">
                I'd like to be an Early Tester (limited spots)
              </label>
              <p className="mt-0.5 text-xs text-text-muted">
                By joining as an Early Tester, you agree that anonymized session data may be used to improve the product after the test period ends.
              </p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-agent-red" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded bg-byron px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-byron-accent disabled:opacity-50"
          >
            {isSubmitting ? 'Joining…' : 'Join Us'}
          </button>
        </form>

        {/* Login link */}
        <p className="mt-6 text-center text-sm text-text-secondary">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-byron underline hover:text-byron-accent">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}

// ----- Helpers -----

function isPasswordValid(pw: string): boolean {
  return (
    pw.length >= 12 &&
    /[A-Z]/.test(pw) &&
    /[a-z]/.test(pw) &&
    /\d/.test(pw) &&
    /[^A-Za-z0-9]/.test(pw)
  );
}
